import React from 'react';
import { ArrowLeft, ShieldCheck, CheckCircle2 } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';

const TermsOfService: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { languageManager } = useLanguage();

    const legal = languageManager.legal;
    const sections = legal.sections;

    const handleBack = () => {
        if (location.key !== 'default') {
            navigate(-1);
        } else {
            navigate('/');
        }
    };

    return (
        <div className="min-h-screen bg-[#FBFBFB] flex flex-col font-sans selection:bg-orange-100">
            {/* Navigation */}
            <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 border-b border-slate-200/60 bg-white/80 backdrop-blur-xl py-3`}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-14">
                        <div className="flex items-center gap-3 cursor-pointer group" onClick={handleBack}>
                            <div className="w-9 h-9 bg-slate-900 rounded-xl flex items-center justify-center shadow-lg shadow-slate-900/10 group-hover:scale-105 transition-transform duration-300">
                                <ShieldCheck className="w-5 h-5 text-white" />
                            </div>
                            <span className="text-lg font-bold text-slate-900 tracking-tight">Lashing Calculator</span>
                        </div>
                        <button
                            onClick={handleBack}
                            className="flex items-center gap-2 text-slate-500 hover:text-slate-900 transition-all font-semibold text-sm px-4 py-2 rounded-xl hover:bg-slate-100"
                        >
                            <ArrowLeft className="w-4 h-4" />
                            {legal.return_home}
                        </button>
                    </div>
                </div>
            </nav>

            <main className="flex-grow pt-20">
                {/* Hero Section */}
                <div className="bg-slate-900 relative overflow-hidden py-16 sm:py-24">
                    <div className="absolute inset-0 opacity-10">
                        <div className="absolute inset-0 bg-[url('/shipping_background.png')] bg-cover bg-center grayscale"></div>
                        <div className="absolute inset-0 bg-gradient-to-b from-slate-900 via-transparent to-slate-900"></div>
                    </div>
                    <div className="max-w-4xl mx-auto px-6 relative z-10 text-center sm:text-left">
                        <div className="flex items-center justify-center sm:justify-start gap-2 mb-4 text-slate-400 font-mono text-xs uppercase tracking-[0.2em]">
                            <span>Information</span>
                            <span>/</span>
                            <span>Legal</span>
                        </div>
                        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tight mb-6">
                            {legal.tos_title}
                        </h1>
                        <div className="flex flex-col sm:flex-row items-center gap-4 text-slate-400 text-sm">
                            <span className="bg-white/10 px-3 py-1 rounded-full border border-white/10 font-medium">
                                {legal.last_updated}
                            </span>
                            <span className="hidden sm:inline opacity-30">|</span>
                            <span className="font-medium">JADE CONSULTANCY AND TRADING LTD</span>
                        </div>
                    </div>
                </div>

                {/* Content Section */}
                <div className="max-w-4xl mx-auto px-6 py-20 lg:py-32">
                    <div className="space-y-20 lg:space-y-28">
                        {legal.detailed_terms ? (
                            legal.detailed_terms.map((term, index) => (
                                <section key={index} className="animate-in fade-in slide-in-from-bottom-4 duration-700 delay-150">
                                    <h2 className="text-[31.5px] font-medium text-[#575656] mb-8 leading-tight tracking-tight">
                                        {!term.title.startsWith('SCHEDULE') && (
                                            <span className="mr-3 opacity-40 font-mono text-2xl">{index + 1}.</span>
                                        )}
                                        {term.title.replace(/^\d+\.\s*/, '')}
                                    </h2>
                                    <div className="space-y-6">
                                        {term.content.map((paragraph, pIndex) => (
                                            <p key={pIndex} className="text-[18px] leading-[26px] text-[#575656] font-normal">
                                                {paragraph}
                                            </p>
                                        ))}
                                    </div>
                                </section>
                            ))
                        ) : (
                            <div className="space-y-20">
                                {/* Fallback content with same styling */}
                                <section>
                                    <h2 className="text-[31.5px] font-medium text-[#575656] mb-8 leading-tight tracking-tight">
                                        <span className="mr-3 opacity-40 font-mono text-2xl">1.</span> {sections.acceptance.title}
                                    </h2>
                                    <p className="text-[18px] leading-[26px] text-[#575656]">
                                        {sections.acceptance.content}
                                    </p>
                                </section>

                                <section>
                                    <h2 className="text-[31.5px] font-medium text-[#575656] mb-8 leading-tight tracking-tight">
                                        <span className="mr-3 opacity-40 font-mono text-2xl">2.</span> {sections.payment_terms.title}
                                    </h2>
                                    <p className="text-[18px] leading-[26px] text-[#575656]">
                                        {sections.payment_terms.content}
                                    </p>
                                </section>

                                <section>
                                    <h2 className="text-[31.5px] font-medium text-[#575656] mb-8 leading-tight tracking-tight">
                                        <span className="mr-3 opacity-40 font-mono text-2xl">3.</span> {sections.termination.title}
                                    </h2>
                                    <p className="text-[18px] leading-[26px] text-[#575656]">
                                        {sections.termination.content}
                                    </p>
                                </section>

                                <section>
                                    <h2 className="text-[31.5px] font-medium text-[#575656] mb-8 leading-tight tracking-tight">
                                        <span className="mr-3 opacity-40 font-mono text-2xl">4.</span> {sections.disclaimer.title}
                                    </h2>
                                    <div className="space-y-6">
                                        <p className="text-[18px] font-bold text-slate-900">
                                            {sections.disclaimer.important}
                                        </p>
                                        <p className="text-[18px] leading-[26px] text-[#575656]">
                                            {sections.disclaimer.content}
                                        </p>
                                    </div>
                                </section>

                                <section>
                                    <h2 className="text-[31.5px] font-medium text-[#575656] mb-8 leading-tight tracking-tight">
                                        <span className="mr-3 opacity-40 font-mono text-2xl">5.</span> {sections.license.title}
                                    </h2>
                                    <p className="mb-8 text-[18px] leading-[26px] text-[#575656]">
                                        {sections.license.subtitle}
                                    </p>
                                    <ul className="space-y-6">
                                        {(sections.license.items as string[]).map((item, i) => (
                                            <li key={i} className="flex gap-4 items-start">
                                                <div className="w-2 h-2 bg-orange-500 rounded-full mt-2.5 shrink-0" />
                                                <span className="text-[18px] leading-[26px] text-[#575656]">{item}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </section>

                                <section>
                                    <h2 className="text-[31.5px] font-medium text-[#575656] mb-8 leading-tight tracking-tight">
                                        <span className="mr-3 opacity-40 font-mono text-2xl">6.</span> {sections.liability.title}
                                    </h2>
                                    <div className="space-y-8">
                                        <p className="text-[18px] leading-[26px] text-[#575656]">
                                            {sections.liability.content}
                                        </p>
                                        <div className="space-y-4">
                                            <p className="font-bold text-slate-900 uppercase tracking-wider text-sm">{sections.liability.cap_title}</p>
                                            <p className="text-[18px] leading-[26px] text-[#575656]">
                                                {sections.liability.cap_content}
                                            </p>
                                        </div>
                                        <div className="space-y-6">
                                            <p className="font-bold text-[#575656] text-[18px]">{sections.liability.not_responsible}</p>
                                            <ul className="grid sm:grid-cols-2 gap-6">
                                                {(sections.liability.not_responsible_items as string[]).map((item, i) => (
                                                    <li key={i} className="flex gap-3 items-start text-[16px] text-[#575656] font-medium">
                                                        <CheckCircle2 className="w-5 h-5 text-orange-400 mt-0.5 shrink-0" />
                                                        <span>{item}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                        </div>
                                    </div>
                                </section>

                                <section>
                                    <h2 className="text-[31.5px] font-medium text-[#575656] mb-8 leading-tight tracking-tight">
                                        <span className="mr-3 opacity-40 font-mono text-2xl">7.</span> {sections.third_party.title}
                                    </h2>
                                    <p className="text-[18px] leading-[26px] text-[#575656] mb-4">
                                        {sections.third_party.content}
                                    </p>
                                    <p className="text-[18px] font-medium text-slate-800">
                                        {sections.third_party.sub_content}
                                    </p>
                                </section>

                                <section>
                                    <h2 className="text-[31.5px] font-medium text-[#575656] mb-8 leading-tight tracking-tight">
                                        <span className="mr-3 opacity-40 font-mono text-2xl">8.</span> {sections.accuracy.title}
                                    </h2>
                                    <p className="text-[18px] leading-[26px] text-[#575656]">
                                        {sections.accuracy.content}
                                    </p>
                                </section>

                                <section>
                                    <h2 className="text-[31.5px] font-medium text-[#575656] mb-8 leading-tight tracking-tight">
                                        <span className="mr-3 opacity-40 font-mono text-2xl">9.</span> {sections.source_responsibility.title}
                                    </h2>
                                    <div className="space-y-8">
                                        <p className="text-[18px] font-bold text-slate-900 border-l-4 border-orange-500 pl-4 py-1 bg-orange-50/50">
                                            {sections.source_responsibility.important}
                                        </p>
                                        <p className="text-[18px] text-[#575656]">{sections.source_responsibility.utilize_content}</p>
                                        <ul className="space-y-6">
                                            {(sections.source_responsibility.data_sources as any[]).map((item, i) => (
                                                <li key={i} className="flex flex-col sm:flex-row gap-2 sm:gap-6 border-b border-slate-100 pb-4">
                                                    <div className="font-bold text-slate-900 min-w-[200px] text-[18px]">{item.title}</div>
                                                    <span className="text-[#575656] text-[18px]">{item.desc}</span>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </section>

                                <section>
                                    <h2 className="text-[31.5px] font-medium text-[#575656] mb-8 leading-tight tracking-tight">
                                        <span className="mr-3 opacity-40 font-mono text-2xl">10.</span> {sections.verification.title}
                                    </h2>
                                    <p className="text-[18px] leading-[26px] text-[#575656]">
                                        {sections.verification.content}
                                    </p>
                                </section>

                                <section>
                                    <h2 className="text-[31.5px] font-medium text-[#575656] mb-8 leading-tight tracking-tight">
                                        <span className="mr-3 opacity-40 font-mono text-2xl">11.</span> {sections.indemnification.title}
                                    </h2>
                                    <p className="text-[18px] leading-[26px] text-[#575656] mb-8">
                                        {sections.indemnification.content}
                                    </p>
                                    <ul className="space-y-4">
                                        {(sections.indemnification.items as string[]).map((item, i) => (
                                            <li key={i} className="flex gap-4 items-center bg-white p-4 rounded-xl border border-slate-100 shadow-sm">
                                                <div className="w-2 h-2 bg-orange-400 rounded-full shrink-0" />
                                                <span className="text-[16px] font-medium text-[#575656]">{item}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </section>

                                <section>
                                    <h2 className="text-[31.5px] font-medium text-[#575656] mb-8 leading-tight tracking-tight">
                                        <span className="mr-3 opacity-40 font-mono text-2xl">12.</span> {sections.governing_law.title}
                                    </h2>
                                    <p className="text-[18px] leading-[26px] text-[#575656]">
                                        {sections.governing_law.content}
                                    </p>
                                </section>
                            </div>
                        )}
                    </div>

                    <div className="mt-28 lg:mt-40 pt-16 border-t border-slate-200 text-center">
                        <p className="text-[#575656] text-lg max-w-2xl mx-auto italic font-medium leading-relaxed">
                            {legal.footer_note}
                        </p>
                    </div>
                </div>
            </main>

            {/* Footer */}
            <footer className="bg-white border-t border-slate-200 py-16 px-6">
                <div className="max-w-4xl mx-auto flex flex-col items-center gap-8 text-center">
                    <div className="flex items-center gap-4 group cursor-pointer" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                        <div className="w-10 h-10 bg-slate-900 rounded-xl flex items-center justify-center shadow-lg shadow-slate-900/10 group-hover:scale-110 transition-transform">
                            <ShieldCheck className="w-6 h-6 text-white" />
                        </div>
                        <span className="text-xl font-bold text-slate-900 tracking-tight">Lashing Calculator</span>
                    </div>
                    <div className="space-y-4 uppercase tracking-[0.3em] text-[10px] font-bold text-slate-400">
                        <p>&copy; {new Date().getFullYear()} Lashing Calculator</p>
                        <p>A service provided by JADE CONSULTANCY AND TRADING LTD</p>
                        <p>All rights reserved</p>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default TermsOfService;
